<template>
  <v-app-bar
    v-if="!themeStore.hideMenuBar && !themeStore.isPublicView"
    app
    color="surface"
    elevation="1"
    height="64"
  >
    <v-app-bar-title>
      <router-link
        to="/manual"
        class="d-flex align-center"
        style="text-decoration: none; color: inherit"
      >
        <!--
          ダークモードでは v-img 自身に白背景を敷く。ラッパーで包むと
          .v-responsive の flex 幅解決が壊れ、ロゴが潰れたり狭い幅で
          切れたりするため、要素構造も props も変えない。
        -->
        <v-img
          v-if="logoUrl"
          :src="logoUrl"
          :alt="brandName"
          max-height="40"
          max-width="160"
          contain
          :class="{ 'brand-logo--on-dark': themeStore.isDarkMode }"
        />
        <span v-else class="text-h6 font-weight-medium">
          {{ brandName }}
        </span>
      </router-link>
    </v-app-bar-title>

    <v-spacer />

    <span
      v-if="authStore.isAuthenticated && !authStore.isGuest"
      class="mr-3 d-none d-sm-inline"
    >
      {{ shownName }}
    </span>

    <v-btn
      v-if="!authStore.isAuthenticated || authStore.isGuest"
      class="mr-2"
      color="primary"
      @click="goToLogin"
    >
      ログイン
    </v-btn>

    <v-menu offset-y>
      <template v-slot:activator="{ props }">
        <v-btn icon v-bind="props">
          <v-icon>mdi-account</v-icon>
        </v-btn>
      </template>
      <v-list>
        <v-list-item v-if="themeStore.isMobile">
          <v-btn variant="text" class="text-left px-4" to="/manual">
            <v-icon start>mdi-book-open-variant</v-icon>
            作業マニュアル
          </v-btn>
        </v-list-item>
        <v-list-item>
          <v-switch
            :model-value="themeStore.isDarkMode"
            label="ダークモード"
            hide-details
            density="comfortable"
            @change="themeStore.toggleTheme"
          />
        </v-list-item>
        <v-list-item v-if="authStore.isAuthenticated && !authStore.isGuest">
          <v-btn variant="text" class="text-left px-4" @click="goToAccountDetail">
            <v-icon start>mdi-account-details</v-icon>
            アカウント詳細
          </v-btn>
        </v-list-item>
        <v-list-item v-if="authStore.userProfile.role === 'admin'">
          <v-btn variant="text" class="text-left px-4" to="/admin/users">
            <v-icon start>mdi-account-cog</v-icon>
            ユーザー管理
          </v-btn>
        </v-list-item>
        <v-list-item v-if="authStore.userProfile.is_superadmin">
          <v-btn variant="text" class="text-left px-4" to="/superadmin/cost">
            <v-icon start>mdi-currency-usd</v-icon>
            コスト
          </v-btn>
        </v-list-item>
        <v-list-item v-if="authStore.userProfile.is_superadmin">
          <v-btn variant="text" class="text-left px-4" to="/superadmin/settings">
            <v-icon start>mdi-shield-account</v-icon>
            認証ポリシー
          </v-btn>
        </v-list-item>
        <v-list-item v-if="authStore.userProfile.is_superadmin">
          <v-btn variant="text" class="text-left px-4" to="/superadmin/staff">
            <v-icon start>mdi-account-star</v-icon>
            superadmin 管理
          </v-btn>
        </v-list-item>
        <v-list-item v-if="authStore.isAuthenticated">
          <v-btn variant="text" class="text-left px-4" @click="onLogout">
            <v-icon start>mdi-logout</v-icon>
            ログアウト
          </v-btn>
        </v-list-item>
      </v-list>
    </v-menu>
  </v-app-bar>
</template>

<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";

import { useAuthStore } from "@/stores/authStore";
import { useThemeStore } from "@/stores/themeStore";
import { useOrganization } from "@/composables/useOrganization";

const qumowillLogoSrc = new URL("@/assets/qumowill-ai_logo_header.png", import.meta.url).href;

const authStore = useAuthStore();
const themeStore = useThemeStore();
const router = useRouter();
// テーマの primary color 反映は引き続き useOrganization に委ねるが、
// ロゴ・ブランド名は Qumowill 単一ブランドに固定する。
useOrganization();
const brandName = "Qumowill AI Manual";
const logoUrl = qumowillLogoSrc;

const shownName = computed(
  () =>
    authStore.userProfile?.display_name ||
    authStore.user?.displayName ||
    authStore.user?.email?.split("@")[0] ||
    "",
);

function goToLogin() {
  router.push({
    path: "/login",
    query: { redirect: router.currentRoute.value.fullPath },
  });
}

function goToAccountDetail() {
  router.push("/account");
}

async function onLogout() {
  await authStore.logout();
}
</script>

<style scoped>
/*
  ロゴは黒基調の透過 PNG で、ダークテーマの AppBar (surface #1E1E1E) と同化して
  読めなくなる。ロゴ画像は Vector Design Inc. の商標なので加工せず、
  下地だけを本来の白に戻す。ライトテーマではこのクラスが付かない。

  padding は使えない。.v-img__img は inset: 0 / height: 100% で border box 全体を
  占めるため内側に寄らず、箱を大きくするとロゴまで拡大されてしまう。
  白地は v-img の箱 (160x40) そのもの。ロゴ実体は 1080x360 (3:1) なので contain で
  実寸 120px となって左右に 20px ずつ空き、さらに画像自身が上下 4.6px / 左右 15px
  相当の余白を持つ。これらが pill の余白を兼ねる。
*/
.brand-logo--on-dark {
  background: #ffffff;
  border-radius: 999px;
}
</style>
